import type { CSSProperties, PointerEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FeatherIcon } from '../../components/FeatherIcon';
import type { WidgetView } from '../board';
import { frameStyle, type FrameState } from './frame';

interface Props {
  frame: FrameState;
  icon: string;
  title: string;
  count?: string | number | null;
  /** Header controls shown outside edit mode (filters, window …). */
  tools?: ReactNode;
  view?: WidgetView;
  onView?: (v: WidgetView) => void;
  onToggleCollapse: () => void;
  onRemove: () => void;
  onMoveStart: (e: PointerEvent) => void;
  onResizeStart: (e: PointerEvent) => void;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  /** Marks the widget as a chat drop target (useChatDrag reads `data-ws-drop`). */
  dropZone?: string;
  banner?: ReactNode;
}

const VIEWS: { v: WidgetView; icon: string }[] = [
  { v: 'list', icon: 'list' },
  { v: 'tiles', icon: 'layout-grid' },
  { v: 'card', icon: 'square' },
];

export function WidgetFrame({ frame, icon, title, count, tools, view, onView, onToggleCollapse, onRemove, onMoveStart, onResizeStart, children, className = '', style, dropZone, banner }: Props) {
  const { t } = useTranslation('workspace');
  const { editing, collapsed } = frame;
  return (
    <section
      className={`ws-widget ${editing ? 'is-editing' : ''} ${frame.dragging || frame.resizing ? 'is-active' : ''} ${className}`}
      style={{ ...frameStyle(frame), ...style }}
      data-ws-drop={dropZone}
    >
      <header className="ws-widget-head" onPointerDown={onMoveStart}>
        {editing && <FeatherIcon name="grip-vertical" size={14} className="ws-grip" />}
        <span className="ws-widget-title">
          <FeatherIcon name={icon} size={15} />
          <span className="ws-widget-name">{title}</span>
          {count != null && <span className="ws-count">{count}</span>}
        </span>
        {!editing && tools}
        {editing && view && onView && (
          <span className="ws-viewswitch" title={t('widget.view')}>
            {VIEWS.map(({ v, icon: ic }) => (
              <button key={v} type="button" className={view === v ? 'is-on' : ''} title={t(`widget.views.${v}`)} onClick={() => onView(v)}>
                <FeatherIcon name={ic} size={12} />
              </button>
            ))}
          </span>
        )}
        <button type="button" className="ws-icon-btn" title={collapsed ? t('widget.expand') : t('widget.collapse')} onClick={onToggleCollapse}>
          <FeatherIcon name={collapsed ? 'chevron-down' : 'chevron-up'} size={14} />
        </button>
        {editing && (
          <button type="button" className="ws-icon-btn" title={t('widget.remove')} onClick={onRemove}>
            <FeatherIcon name="x" size={14} />
          </button>
        )}
      </header>
      {!collapsed && (
        <>
          {banner}
          <div className="ws-widget-body">{children}</div>
        </>
      )}
      {editing && !collapsed && (
        <span className="ws-resize" title={t('widget.resize')} onPointerDown={onResizeStart}>
          <FeatherIcon name="move-diagonal-2" size={12} />
        </span>
      )}
    </section>
  );
}
